import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { Organization, UserProfile } from '../../types'
import {
  Alert, Card, CardBody, CardHeader, EmptyState, Input, Skeleton, StatusBadge,
} from '../../components/ui'
import { UserIcon } from '../../components/icons'

const TABS = [
  { value: '', label: 'All people' },
  { value: 'officer', label: 'Gov officers' },
  { value: 'company', label: 'Organisations' },
]

export default function Users() {
  const [tab, setTab] = useState('')
  const [search, setSearch] = useState('')

  const usersQuery = useQuery({
    queryKey: ['gov', 'users', tab, search],
    queryFn: () => api.get<{ users: UserProfile[]; count: number }>(
      `/users?limit=200${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    ),
    enabled: tab !== 'company',
  })
  const orgsQuery = useQuery({
    queryKey: ['gov', 'orgs-tab', search],
    queryFn: () => api.get<{ organizations: Organization[]; count: number }>(
      `/organizations?limit=200`,
    ),
    enabled: tab === 'company',
  })

  const _GOV_ROLES = new Set(['gov_director', 'gov_regional', 'gov_inspector'])
  let people = usersQuery.data?.users ?? []
  if (tab === 'officer') people = people.filter((u) => _GOV_ROLES.has(u.role ?? ''))
  if (search && tab !== 'company') {
    const q = search.toLowerCase()
    people = people.filter((u) => (u.full_name ?? '').toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q))
  }
  const orgs = (orgsQuery.data?.organizations ?? []).filter((o) => !search || (o.name ?? '').toLowerCase().includes(search.toLowerCase()))

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users &amp; organisations</h1>
          <p className="page-head__sub">Everyone registered in your jurisdiction.</p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-4)', gap: 'var(--sp-2)' }}>
        {TABS.map((t) => (
          <button
            key={t.value}
            className={`btn btn--sm ${tab === t.value ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1, minWidth: 200 }}>
          <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardHeader title={tab === 'company' ? 'Organisations' : 'People'} />
        <CardBody flush>
          {tab === 'company' ? (
            orgsQuery.isError ? (
              <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(orgsQuery.error as Error).message}</Alert></div>
            ) : orgsQuery.isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
            ) : orgs.length === 0 ? (
              <EmptyState title="No organisations found" body="" />
            ) : (
              <>
                {/* PWA tap rows (Status clipped off the 4-col table at
                    390px); desktop keeps the table. Same below for people. */}
                <div className="mobile-only">
                  {orgs.map((o) => (
                    <Link key={o.id} to={`/organizations/${o.id}`} className="lrow">
                      <div className="lrow__main">
                        <div className="lrow__title">{o.name}</div>
                        <div className="lrow__sub">
                          {[titleCase(o.type),
                            [titleCase(o.district_id), titleCase(o.region_id)].filter(Boolean).join(', ')]
                            .filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div className="lrow__end"><StatusBadge status={o.status} /></div>
                    </Link>
                  ))}
                </div>
                <div className="table-wrap desktop-only">
                  <table className="table">
                    <thead><tr><th>Organisation</th><th>Type</th><th>Location</th><th>Status</th></tr></thead>
                    <tbody>
                      {orgs.map((o) => (
                        <tr key={o.id}>
                          <td><Link to={`/organizations/${o.id}`} style={{ fontWeight: 600 }}>{o.name}</Link></td>
                          <td>{titleCase(o.type)}</td>
                          <td>{[titleCase(o.district_id), titleCase(o.region_id)].filter(Boolean).join(', ') || '—'}</td>
                          <td><StatusBadge status={o.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          ) : usersQuery.isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(usersQuery.error as Error).message}</Alert></div>
          ) : usersQuery.isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
          ) : people.length === 0 ? (
            <EmptyState icon={<UserIcon size={22} />} title="No people found" body="" />
          ) : (
            <>
              <div className="mobile-only">
                {people.map((u) => (
                  <Link key={u.uid} to={`/users/${u.uid}`} className="lrow">
                    <div className="lrow__main">
                      <div className="lrow__title">{u.full_name || u.email}</div>
                      <div className="lrow__sub">
                        {[titleCase(u.role),
                          [titleCase(u.district_id), titleCase(u.region_id)].filter(Boolean).join(', ')]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="lrow__end"><StatusBadge status={u.status} /></div>
                  </Link>
                ))}
              </div>
              <div className="table-wrap desktop-only">
                <table className="table">
                  <thead><tr><th>Name</th><th>Role</th><th>Location</th><th>Status</th></tr></thead>
                  <tbody>
                    {people.map((u) => (
                      <tr key={u.uid}>
                        <td><Link to={`/users/${u.uid}`} style={{ fontWeight: 600 }}>{u.full_name || u.email}</Link></td>
                        <td>{titleCase(u.role)}</td>
                        <td>{[titleCase(u.district_id), titleCase(u.region_id)].filter(Boolean).join(', ') || '—'}</td>
                        <td><StatusBadge status={u.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
